import type {
  FinancialChargeRole,
  FinancialExportKind,
  FinancialExportScope,
  FinancialLine,
} from '@/types/Models/Financial'
import { strToU8, zipSync } from 'fflate'
import {
  financialDecimal,
  financialPercent,
  roundFinancialAmount,
  subtractFinancialAmounts,
  sumFinancialAmounts,
} from '@/services/FinancialAmounts'
import { financialBillingKey } from '@/services/FinancialBilling'
import { financialWeightGrams } from '@/services/FinancialComparison'
import {
  financialBillingName,
  financialChargeAmount,
  financialInvoiceLabel,
  financialLocalDate,
  financialReady,
  financialRole,
} from '@/services/FinancialImport'
import { financialBillingState } from '@/services/FinancialSignals'

const ROLE_LABELS: Record<FinancialChargeRole, string> = {
  base: 'Delivery',
  additional: 'Additional charge',
  credit: 'Credit',
}

// Whether the row was in an earlier billing basis, and at which amount when it changed since.
const billedText = (batch: FinancialLine['batch'], rowId: string): string => {
  const state = financialBillingState(batch, rowId)
  if (state === 'billed') return 'Yes'
  if (state !== 'changed') return 'No'
  const record = [...batch.exports].reverse().find((item) => item.kind !== 'review' && item.rowIds?.includes(rowId))
  const amount = record?.snapshot?.[rowId]?.customerPrice
  return `Yes – changed after billing${amount ? ` (billed at ${amount})` : ''}`
}

const priceBasis = (line: FinancialLine): string => {
  const review = line.row.review
  if (review?.status !== 'approved') return ''
  if (review.priceSource === 'booked') return 'PacSort booked price'
  if (review.priceSource === 'margin') return `Carrier cost + ${review.marginPercent ?? '?'}% customer margin`
  return 'Entered by reviewer'
}

// One shipment after another, each with its delivery first and later fees in date order.
const byShipment = (lines: FinancialLine[]): FinancialLine[] =>
  [...lines].sort(
    (a, b) =>
      (a.row.match?.itemId ?? a.row.trackingNumber).localeCompare(b.row.match?.itemId ?? b.row.trackingNumber) ||
      Number(financialRole(a.batch, a.row) !== 'base') - Number(financialRole(b.batch, b.row) !== 'base') ||
      a.row.date.localeCompare(b.row.date),
  )

// Numbers are written as exact decimals at 1/100 öre: no binary artefacts such as 27.664000000000001.
const cellNumber = (value: string): string | null => {
  const exact = financialDecimal(value)
  if (exact === null) return null
  const rounded = roundFinancialAmount(exact)
  return rounded.includes('.') ? rounded.replace(/0+$/, '').replace(/\.$/, '') : rounded
}

const xml = (value: string): string => {
  const allowed = Array.from(value)
    .filter((character) => {
      const code = character.codePointAt(0) ?? 0
      return code === 9 || code === 10 || code === 13 || (code >= 32 && code !== 65534 && code !== 65535)
    })
    .join('')
  return allowed.replaceAll('&', '&amp;').replaceAll('<', '&lt;').replaceAll('>', '&gt;').replaceAll('"', '&quot;')
}

const columnName = (index: number): string => {
  let number = index + 1
  let name = ''
  while (number) {
    const remainder = (number - 1) % 26
    name = String.fromCharCode(65 + remainder) + name
    number = Math.floor((number - 1) / 26)
  }
  return name
}

interface FinancialSheet {
  name: string
  rows: string[][]
  numeric: number[]
}

// Inline text cells preserve leading zeroes and never execute source values as formulas.
const workbook = (sheets: FinancialSheet[]): Uint8Array => {
  const files: Record<string, Uint8Array> = {
    '[Content_Types].xml': strToU8(
      '<?xml version="1.0"?><Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types"><Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/><Default Extension="xml" ContentType="application/xml"/><Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>' +
        sheets
          .map(
            (_, i) =>
              `<Override PartName="/xl/worksheets/sheet${i + 1}.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>`,
          )
          .join('') +
        '</Types>',
    ),
    '_rels/.rels': strToU8(
      '<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/></Relationships>',
    ),
    'xl/workbook.xml': strToU8(
      '<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships"><sheets>' +
        sheets.map((sheet, i) => `<sheet name="${xml(sheet.name)}" sheetId="${i + 1}" r:id="rId${i + 1}"/>`).join('') +
        '</sheets></workbook>',
    ),
    'xl/_rels/workbook.xml.rels': strToU8(
      '<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">' +
        sheets
          .map(
            (_, i) =>
              `<Relationship Id="rId${i + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet${i + 1}.xml"/>`,
          )
          .join('') +
        '</Relationships>',
    ),
  }
  sheets.forEach((sheet, index) => {
    const width = Math.max(...sheet.rows.map((cells) => cells.length))
    files[`xl/worksheets/sheet${index + 1}.xml`] = strToU8(
      `<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main"><sheetViews><sheetView workbookViewId="0"><pane ySplit="1" topLeftCell="A2" state="frozen"/></sheetView></sheetViews><cols><col min="1" max="${width}" width="22" customWidth="1"/></cols><sheetData>` +
        sheet.rows
          .map(
            (cells, rowIndex) =>
              `<row r="${rowIndex + 1}">` +
              cells
                .map((cell, column) => {
                  const reference = `${columnName(column)}${rowIndex + 1}`
                  const number = rowIndex > 0 && sheet.numeric.includes(column) ? cellNumber(cell) : null
                  if (number !== null) return `<c r="${reference}" t="n"><v>${number}</v></c>`
                  return `<c r="${reference}" t="inlineStr"><is><t xml:space="preserve">${xml(cell)}</t></is></c>`
                })
                .join('') +
              '</row>',
          )
          .join('') +
        '</sheetData></worksheet>',
    )
  })
  return zipSync(files)
}

const internalRows = (lines: FinancialLine[], scope: FinancialExportScope): string[][] => {
  // Count each identified parcel once in this file, on its first delivery row. Fees and credits
  // never add parcels. A missing identifier stays unknown instead of inventing a quantity.
  const counted = new Set<string>()
  return [
    [
      'Invoice',
      'Carrier',
      'Currency (confirmed)',
      'Customer',
      'Customer ID',
      'Sender address',
      'Tracking number',
      'Carrier reference',
      'PacSort item reference',
      'Assignment reference',
      'Service / charge',
      'Charge type',
      'Charge date',
      'Actual carrier cost (this row)',
      'Quoted shipment price (SEK)',
      'Purchase tariff (manual)',
      'Customer amount to invoice (this row)',
      'Margin (approved customer amount − cost)',
      'Invoice weight (g)',
      'Booked weight (g)',
      'Weight difference (g)',
      'Margin % on cost',
      'Price basis',
      'Match status',
      'Review status',
      'Review note',
      'Reviewed by',
      'Reviewed at',
      'In earlier billing export',
      'Source file',
      'Worksheet',
      'Source row',
      'File imported at',
      'File imported by',
      'Previously billed customer amount',
      'Approved customer amount (cumulative for this row)',
      'Package count',
      'Package count basis',
      'Booking date',
    ],
    ...lines.map(({ batch, row }) => {
      const cost = financialChargeAmount(batch, row)
      const customer = row.review?.customerPrice ?? null
      const invoiceWeight = financialWeightGrams(batch, row)
      const bookedWeight = row.match?.weightGrams ?? null
      const delivery = financialRole(batch, row) === 'base'
      const parcel = row.trackingNumber ? `${batch.carrier}:${row.trackingNumber}` : ''
      const quantity = !delivery ? '0' : !parcel ? '' : counted.has(parcel) ? '0' : '1'
      const quantityBasis = !delivery ? 'Fee or credit — no additional package'
        : !parcel ? 'Unknown — no shipment number'
          : counted.has(parcel) ? 'Already counted in this report' : 'Unique carrier and shipment number'
      if (delivery && parcel) counted.add(parcel)
      return [
        batch.invoiceNumber,
        batch.carrier,
        batch.currency,
        row.match ? financialBillingName(row.match) : '',
        row.match?.companyId ?? '',
        row.match?.senderAddressName ?? '',
        row.trackingNumber,
        row.reference,
        row.match?.itemReference ?? '',
        row.match?.assignmentReference ?? '',
        row.description,
        ROLE_LABELS[financialRole(batch, row)],
        row.date,
        cost ?? '',
        row.match?.bookedPrice ?? '',
        row.review?.purchasePrice ?? '',
        scope.billingAmounts?.[financialBillingKey({ batch, row })]?.amount ?? customer ?? '',
        customer !== null && cost !== null ? subtractFinancialAmounts(customer, cost) : '',
        invoiceWeight === null ? '' : String(invoiceWeight),
        bookedWeight === null ? '' : String(bookedWeight),
        invoiceWeight === null || bookedWeight === null ? '' : String(Math.round((invoiceWeight - bookedWeight) * 1000) / 1000),
        customer !== null && cost !== null ? (financialPercent(subtractFinancialAmounts(customer, cost), cost) ?? '') : '',
        priceBasis({ batch, row }),
        row.matchStatus,
        row.review?.status ?? 'pending',
        row.review?.note ?? '',
        row.review?.userName ?? '',
        row.review?.reviewedAt ?? '',
        billedText(batch, row.id),
        batch.fileName,
        row.sheet,
        String(row.rowNumber),
        batch.createdAt,
        batch.createdByName,
        scope.billingAmounts?.[financialBillingKey({ batch, row })]?.previous ?? '',
        customer ?? '',
        quantity,
        quantityBasis,
        row.match ? financialLocalDate(row.match.createdAt) : '',
      ]
    }),
    ...(scope.quotedShipments ?? []).map((shipment) => [
      '', shipment.carrier, 'SEK', shipment.billingName, shipment.companyId, shipment.senderAddressName ?? '',
      shipment.trackingNumbers.join(', '), '', shipment.itemReference, shipment.assignmentReference,
      'Quoted booking — carrier delivery invoice pending', 'Delivery quote', financialLocalDate(shipment.bookedAt),
      '', shipment.bookedPrice!, '', shipment.bookedPrice!, '', '',
      shipment.weightGrams === null ? '' : String(shipment.weightGrams), '', '', 'Quoted booking',
      'PacSort booking', 'Approved by billing export', '', '', '', 'See quoted billing history', '', '', '', '', '', '0', shipment.bookedPrice!,
      '', 'Quoted booking — package count is based on carrier delivery rows', financialLocalDate(shipment.bookedAt),
    ]),
  ]
}

export const financialExportWorkbook = (
  lines: FinancialLine[],
  kind: FinancialExportKind,
  scope: FinancialExportScope = {},
): Uint8Array => {
  const matchedReview = kind === 'review' && scope.reviewSelection === 'matched'
  const quotedShipments = kind === 'review' ? [] : scope.quotedShipments ?? []
  const billedAmount = (line: FinancialLine): string => scope.billingAmounts?.[financialBillingKey(line)]?.amount ?? line.row.review!.customerPrice!
  const rows = kind === 'review'
    ? matchedReview ? lines.filter(({ row }) => row.matchStatus === 'matched' && !!row.match) : lines
    : lines.filter(({ batch, row }) => financialReady(batch, row))
  if (!rows.length && !quotedShipments.length) throw new Error(matchedReview ? 'There are no matched rows to export.' : kind === 'review' ? 'There are no rows to export.' : 'There are no approved rows to export.')
  const batches = [...new Map(rows.map(({ batch }) => [batch.id, batch])).values()]
  const currencies = [...new Set([...batches.map((batch) => batch.currency), ...quotedShipments.map(() => 'SEK')])]
  if (kind === 'specification') {
    if (currencies.length !== 1) throw new Error('A customer specification needs rows in one currency.')
    // Shipment level: every shipment with its booking data, each charge on its own row and a total per shipment.
    const sorted = byShipment(rows)
    const shipmentOf = (line: FinancialLine): string => line.row.match?.itemId ?? line.row.trackingNumber
    const totals = new Map<string, string>()
    for (const line of sorted)
      totals.set(shipmentOf(line), sumFinancialAmounts([totals.get(shipmentOf(line)) ?? '0', billedAmount(line)]))
    for (const shipment of quotedShipments) totals.set(shipment.itemId, sumFinancialAmounts([totals.get(shipment.itemId) ?? '0', shipment.bookedPrice!]))
    const total = sumFinancialAmounts([...rows.map(billedAmount), ...quotedShipments.map((shipment) => shipment.bookedPrice!)])
    return workbook([
      {
        name: 'Specification',
        numeric: [5, 10, 12, 13],
        rows: [
          [
            'Shipment number',
            'Your reference',
            'Booked',
            'Sender address',
            'Service',
            'Booked weight (g)',
            'Charge',
            'Charge type',
            'Charge date',
            'Carrier',
            'Amount',
            'Currency',
            'Shipment amount in this export',
            'Quoted shipment price (SEK)',
          ],
          ...sorted.map((line, index) => {
            const { batch, row } = line
            const last = index === sorted.length - 1 || shipmentOf(sorted[index + 1]) !== shipmentOf(line)
            return [
              row.trackingNumber,
              row.match?.itemReference ?? '',
              row.match ? financialLocalDate(row.match.createdAt) : '',
              row.match?.senderAddressName ?? '',
              row.match?.service ?? '',
              row.match?.weightGrams == null ? '' : String(row.match.weightGrams),
              row.description,
              ROLE_LABELS[financialRole(batch, row)],
              row.date,
              batch.carrier,
              billedAmount(line),
              batch.currency,
              last && !quotedShipments.some((shipment) => shipment.itemId === shipmentOf(line)) ? totals.get(shipmentOf(line))! : '',
              last && !quotedShipments.some((shipment) => shipment.itemId === shipmentOf(line)) ? row.match?.bookedPrice ?? '' : '',
            ]
          }),
          ...quotedShipments.map((shipment) => [
            shipment.trackingNumbers.join(', '), shipment.itemReference, financialLocalDate(shipment.bookedAt),
            shipment.senderAddressName ?? '', shipment.service, shipment.weightGrams === null ? '' : String(shipment.weightGrams),
            'Delivery at quoted price', 'Delivery', financialLocalDate(shipment.bookedAt), shipment.carrier,
            shipment.bookedPrice!, 'SEK', totals.get(shipment.itemId)!, shipment.bookedPrice!,
          ]),
          ['Total', '', '', '', '', '', '', '', '', '', total, currencies[0], total, ''],
        ],
      },
      {
        name: 'About',
        numeric: [],
        rows: [
          ['Customer specification', scope.customerName ?? ''],
          ['Import period', scope.period || 'All import dates'],
          ['Generated', new Date().toISOString()],
          ['Shipments', String(totals.size)],
          ['Rows', String(rows.length + quotedShipments.length)],
          ['Amounts', 'Customer amount per charge, excluding VAT; shipment amounts cover only this export, not the final cost of the shipment. Later fees or credits can appear in later periods.'],
          ['Quoted price', 'Original customer price from the PacSort booking, shown once per shipment in SEK for reference. Delivery at quoted price rows bill that quote when first exported. The reference column is never added to Amount.'],
          ['Adjustments', 'Amount includes only the approved customer adjustment after earlier billing. Quoted prices remain unchanged.'],
          ['Cut-off', 'Import date in Europe/Stockholm, including older unbilled rows and corrections. The carrier charge date is retained separately.'],
        ],
      },
    ])
  }
  const metadata = [
    [
      'Financial',
      kind === 'review'
        ? matchedReview
          ? 'Matched reconciliation working draft — may contain unapproved or excluded rows; not a billing basis'
          : 'Reconciliation working draft — includes unresolved and excluded rows'
        : 'Internal billing basis — contains carrier costs; not marked as invoiced',
    ],
    ['Generated', new Date().toISOString()],
    ['Customer scope', scope.customerName ?? 'All customers'],
    ['Import period', kind === 'review' ? 'Selected invoices, regardless of the booking period shown on screen' : scope.period || 'All import dates'],
    ['Cut-off', kind === 'review' ? 'No booking-date or charge-date filter. All rows in the selected invoice and customer scope are included.' : 'Import date in Europe/Stockholm, including older unbilled rows and corrections. New invoice rows for a previously billed shipment remain eligible for a later period.'],
    ['Quoted price', 'Original customer price from the PacSort booking in SEK, independent of imported carrier cost and the reviewed customer amount. Repeated on charge rows for reference; do not sum quotes across fees or periods.'],
    ['Package count', 'Sum Package count for unique carrier/shipment numbers with a delivery row in this report. Fees, credits and repeated delivery rows count as 0. Missing shipment numbers have an unknown count (blank); use Package count basis to check them. Filtering out the counted delivery row removes that package from the sum.'],
    ['Booking date', 'When the shipment was booked in PacSort, in Europe/Stockholm. This can be earlier than the charge date or invoice month.'],
    ['Actual cost', 'Carrier cost from the imported source rows, in the invoice currency. Later charges or credits can change the shipment cost; an export is not a final shipment settlement.'],
    ['Invoices', batches.map((batch) => `${batch.carrier} ${financialInvoiceLabel(batch)}`).join('; ')],
    ['Source SHA-256', batches.map((batch) => batch.fileHash).join('; ')],
    ['Imported by', [...new Set(batches.map((batch) => `${batch.createdByName} ${batch.createdAt}`))].join('; ')],
    [
      'Amount basis',
      batches
        .map((batch) =>
          batch.carrier === 'PostNord'
            ? `PostNord: ${batch.amountBasis || 'unconfirmed — Gross amount shown provisionally'}`
            : batch.carrier === 'CityMail'
              ? 'CityMail: Paket pris'
              : batch.carrier === 'Deutsche Post'
                ? 'Deutsche Post: Net Price, including separate surcharge rows'
                : 'Earlybird: Price + separate FeeAmount rows',
        )
        .join('; '),
    ],
    ['Rows in this export', String(rows.length)],
    ...(kind === 'review' ? [
      ['Review selection', matchedReview ? 'Matched shipments only; unresolved rows are omitted' : 'All rows in the selected scope'],
      ['Approval state', 'Downloading does not approve or change any row. See Review status for each row.'],
    ] : []),
    ['Rows already in an earlier billing export', String(scope.previouslyBilled ?? 0)],
    [
      'Carrier cost outside this export',
      scope.uncoveredAmount === undefined
        ? 'Not calculated'
        : `${scope.uncoveredAmount ?? 'Invalid amounts'} across ${scope.uncoveredRows ?? 0} ${kind === 'review' ? 'omitted rows' : 'rows without approved revenue'}`,
    ],
    ['Saved scope', 'This browser and staff account; not a shared ledger'],
    [
      'Pricing',
      'Quoted bookings may be billed before delivery costs arrive. Carrier rows use the reviewed customer amount. Only the difference from prior customer billing is included; actual carrier cost never silently replaces the quote. Each approval records its price basis.',
    ],
    ['Billing state', 'Downloading does not invoice a customer. Verify prior billing in Jeeves before use.'],
  ]
  const outside = kind === 'review' ? [] : byShipment(scope.outside ?? [])
  return workbook([
    {
      name: kind === 'review' ? 'Reconciliation' : 'Billing basis (internal)',
      numeric: [13, 14, 15, 16, 17, 18, 19, 20, 21, 34, 35, 36],
      rows: internalRows(kind === 'review' ? rows : byShipment(rows), { ...scope, quotedShipments }),
    },
    {
      name: 'Quoted prices', numeric: [3], rows: [
        ['PacSort item', 'Shipment number', 'Customer', 'Quoted customer price', 'Currency'],
        ...[...new Map([
          ...rows.filter((line) => line.row.match).map(({ row }) => [row.match!.itemId, [row.match!.itemId, row.trackingNumber, financialBillingName(row.match!), row.match!.bookedPrice ?? '', 'SEK']] as const),
          ...quotedShipments.map((shipment) => [shipment.itemId, [shipment.itemId, shipment.trackingNumbers.join(', '), shipment.billingName, shipment.bookedPrice!, 'SEK']] as const),
        ]).values()].map((row) => [...row]),
      ],
    },
    {
      name: 'Actual carrier costs', numeric: [5], rows: [
        ['Invoice', 'Carrier', 'Shipment number', 'Charge', 'Charge date', 'Actual cost', 'Currency', 'Imported at'],
        ...rows.map(({ batch, row }) => [batch.invoiceNumber, batch.carrier, row.trackingNumber, row.description, row.date, financialChargeAmount(batch, row) ?? '', batch.currency, batch.createdAt]),
      ],
    },
    ...(outside.length
      ? [
          {
            name: 'Not included',
            numeric: [4],
            rows: [
              ['Tracking number', 'PacSort item reference', 'Charge', 'Charge date', 'Invoice cost', 'Status', 'Invoice'],
              ...outside.map(({ batch, row }) => [
                row.trackingNumber,
                row.match?.itemReference ?? '',
                row.description,
                row.date,
                financialChargeAmount(batch, row) ?? '',
                row.review?.status === 'excluded' ? `Excluded: ${row.review.note}` : 'Not approved',
                `${batch.carrier} ${financialInvoiceLabel(batch)}`,
              ]),
            ],
          },
        ]
      : []),
    { name: 'About this export', numeric: [], rows: metadata },
  ])
}

export const financialExportFileName = (kind: FinancialExportKind, parts: string[]): string => {
  const label = kind === 'review' ? 'review' : kind === 'billing' ? 'billing-internal' : 'specification'
  return `Financial-${[...parts.filter(Boolean), label].join('-')}.xlsx`.replace(/[\\/:*?"<>|\s]+/g, '-')
}

export const downloadFinancialFile = (blob: Blob, name: string): void => {
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = name.replace(/[\\/:*?"<>|]/g, '-')
  link.click()
  window.setTimeout(() => URL.revokeObjectURL(url), 1000)
}
